import React, { useState, useCallback, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import { AgenticLeftPanelProps, DEFAULT_INPUT_PLACEHOLDER } from '../AskAsperAgentic.types';
import { useSlashLookup, replaceSlashTokenQuery } from '../SearchBar/useSlashLookup';
import LookupSuggestionDropdown from '../SearchBar/LookupSuggestionDropdown';
import LookupCategoryDropdown from '../SearchBar/LookupCategoryDropdown';
import LookupHighlightLayer from '../SearchBar/LookupHighlightLayer';
import { useLookupInputRanges } from '../SearchBar/useLookupInputRanges';
import { createMixpanelEventFirer } from '../utils/mixpanelEvents';
import {
    fireEntityFilterItemDeselect,
    fireEntityFilterItemSelect,
    fireSlashCommandOptionSelect,
    fireTimePeriodSelect,
    isTimePeriodLookupItem,
} from '../utils/slashCommandMixpanel';
import type {
    AgenticEntityDisambiguationMatch,
    AgenticEntityDisambiguationTerm,
    LookupItem,
} from '../AskAsperAgentic.types';
import { applyEntityDisambiguationSelections } from '../utils/lookupTokenMarkers';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Typography + box-model classes shared between the in-conversation input textarea
 *  and its `LookupHighlightLayer` mirror. Must stay identical so the highlight pills
 *  align pixel-perfectly with the underlying glyphs. */
const LOOKUP_INPUT_TYPOGRAPHY =
    "font-['Open_Sans'] font-normal text-[16px] leading-6 m-0 p-0";

/** Format ISO 8601 timestamp to "Jan 13, 2026 at 10:42 AM" for display (matches Figma design) */
function formatTimestampForDisplay(isoOrFormatted: string): string {
    try {
        // If already in human-readable format (e.g. "Jan 13, 2026 at 10:42 AM"), pass through
        if (/[A-Za-z]{3}\s+\d{1,2},\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s+(?:AM|PM)/i.test(isoOrFormatted)) {
            return isoOrFormatted;
        }
        const date = new Date(isoOrFormatted);
        if (Number.isNaN(date.getTime())) return isoOrFormatted;
        const month = MONTHS[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        const minStr = String(minutes).padStart(2, '0');
        return `${month} ${day}, ${year} at ${hours}:${minStr} ${ampm}`;
    } catch {
        return isoOrFormatted;
    }
}
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { extractTextContent, fixUnclosedColorSpans } from '../../../shared/markdownComponents';
import { markdownSanitizeSchema } from '../../../shared/markdownSanitizeSchema';
import HighlightBadge from '../../../atoms/HighlightBadge/HighlightBadge';
import ConversationMessage from './ConversationMessage';
import TypewriterText from '../../../atoms/TypewriterText';
import HITLCard from './HITLCard';
import EntityDisambiguationCard from './EntityDisambiguationCard';
import OutOfScopeFeedbackCard from './OutOfScopeFeedbackCard';
import PlannerTasksBox from './PlannerTasksBox';
import PhaseCard from './PhaseCard';
import AssessmentMessage from './AssessmentMessage';
import FollowupQuestions from './FollowupQuestions';
import StandaloneSummaryCard from './StandaloneSummaryCard';
import EditDoc from '../../../atoms/Icon/EditDoc';
import Menu from '../../../atoms/Icon/Menu';
import Tooltip from '../../../atoms/Tooltip/Tooltip';
import Search from '../../../atoms/Icon/Search';
import Bookmark from '../../../atoms/Icon/Bookmark';
import Send from '../../../atoms/Icon/Send';
import QuestionCircleOutlined from '../../../atoms/Icon/QuestionCircleOutlined';
import useNotification from '../../../atoms/Notification/useNotification';

/**
 * AgenticLeftPanel - Left panel component for the Agentic Ask Asper two-panel interface
 *
 * This component displays:
 * - Conversation history (user queries and AskAsper responses)
 * - Running task text (green text showing LLM actions)
 * - Planner tasks (grey box showing upcoming tasks)
 * - Phase cards (collapsible cards with subtasks)
 * - Analysis steps in card format (legacy support)
 * - Phase completion status (legacy support)
 * - HITL (Human in the Loop) cards as interrupts
 *
 * The component is designed to be extendible and clean, supporting various content types
 * that can be dynamically inserted into the conversation flow.
 */
const AgenticLeftPanel: React.FC<AgenticLeftPanelProps> = ({
    headerTitle,
    onHeaderActionClick,
    showSidebarToggleInPanel = false,
    onSidebarToggleInPanel,
    conversationTitle,
    timestamp,
    conversationMessages = [],
    conversationPairs,
    runningTasks = [],
    plannerTasks,
    phaseCards = [],
    analysisSteps = [],
    phaseCompleteStatus,
    hitlCards = [],
    showTasks = true,
    onToggleTasks,
    onViewArtefact,
    onTogglePhase,
    onEntityDisambiguationContinue,
    onEntityDisambiguationSkip,
    synthesisItems = [],
    assessmentStatus,
    assessmentContent,
    suggestedActions = [],
    onSuggestedActionClick,
    onPhaseToggleCollapsed,
    standaloneSummary,
    standaloneSummaryTimestamp,
    standaloneFollowUpQuestions = [],
    showConversationTitleAsCard = false,
    bottomContent,
    inputValue: controlledInputValue,
    onInputChange,
    onInputSubmit,
    onQuestionBankClick,
    onSaveAsCustom,
    inputPlaceholder = DEFAULT_INPUT_PLACEHOLDER,
    inputDisabled = false,
    isLoading = false,
    searchDisclaimer,
    enableTypewriter = true,
    typewriterSpeed = 10,
    lookupItems,
    selectedLookups,
    onLookupsChange,
    onLoadLookups,
    lookupCategories,
    lookupDimensionGroups,
    onTrackEvent,
    analyticsThreadId,
    analyticsSessionId,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const { success: showSuccessNotification } = useNotification();
    const [internalInputValue, setInternalInputValue] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const prevPairCountRef = useRef<number>(0);
    /** When true, user has manually scrolled up - disable auto-scroll until next question */
    const userHasScrolledUpRef = useRef(false);
    /** Timestamp of last programmatic scroll - used to ignore our own scroll events */
    const lastProgrammaticScrollRef = useRef<number>(0);

    const SCROLL_NEAR_BOTTOM_THRESHOLD = 80;

    const inputValue = controlledInputValue !== undefined ? controlledInputValue : internalInputValue;
    const hasInputValue = inputValue && inputValue.trim().length > 0;
    const canSubmit = hasInputValue && !inputDisabled && !isLoading;

    // ----- Slash-command lookup wiring -----
    const inputTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [inputCaretPos, setInputCaretPos] = useState(0);
    const pendingInputCaretRef = useRef<number | null>(null);
    const hasLoadedLookupsRef = useRef(false);
    const selectedLookupsRef = useRef(selectedLookups);
    selectedLookupsRef.current = selectedLookups;

    const fireEvent = useMemo(() => createMixpanelEventFirer(onTrackEvent), [onTrackEvent]);
    const analyticsContext = useMemo(
        () => ({ threadId: analyticsThreadId, sessionId: analyticsSessionId }),
        [analyticsThreadId, analyticsSessionId],
    );

    const handleLookupRemoved = useCallback(
        (item: LookupItem) => {
            fireEntityFilterItemDeselect(fireEvent, item, analyticsContext);
        },
        [fireEvent, analyticsContext],
    );

    const slash = useSlashLookup(inputValue, inputCaretPos, lookupItems, {
        categories: lookupCategories,
        dimensionGroups: lookupDimensionGroups,
    });

    const lookupRanges = useLookupInputRanges({
        inputValue,
        selectedLookups,
        onLookupsChange,
        onLookupRemoved: handleLookupRemoved,
    });

    useLayoutEffect(() => {
        if (pendingInputCaretRef.current !== null && inputTextareaRef.current) {
            const pos = pendingInputCaretRef.current;
            inputTextareaRef.current.setSelectionRange(pos, pos);
            setInputCaretPos(pos);
            pendingInputCaretRef.current = null;
        }
    });

    const syncInputCaretFromTextarea = useCallback(() => {
        const el = inputTextareaRef.current;
        if (!el) return;
        setInputCaretPos(el.selectionStart ?? 0);
    }, []);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = e.target.value;
            // Rebase entity ranges against the user edit BEFORE the value prop
            // flips, so range positions stay consistent with the string they
            // describe.
            lookupRanges.onEdit(inputValue, newValue);
            if (onInputChange) {
                onInputChange(newValue);
            } else {
                setInternalInputValue(newValue);
            }
            setInputCaretPos(e.target.selectionStart ?? newValue.length);
        },
        [inputValue, lookupRanges, onInputChange],
    );

    const handleInputSubmit = useCallback(() => {
        if (canSubmit && onInputSubmit) {
            onInputSubmit(inputValue);
            // Clear input after send (works for both controlled and uncontrolled modes)
            if (onInputChange) {
                onInputChange('');
            } else {
                setInternalInputValue('');
            }
        }
    }, [canSubmit, onInputSubmit, onInputChange, inputValue]);

    /**
     * Apply close-match selections to the original user query, mirror the
     * clarified text into the search bar, and resume via the host callback
     * with `resolvedUserInput` so the subsequent call is not `"proceed"`.
     */
    const handleEntityDisambiguationContinue = useCallback(
        (
            selected: AgenticEntityDisambiguationMatch[],
            messageId: string | number | undefined,
            terms: AgenticEntityDisambiguationTerm[],
            originalUserContent: string,
        ) => {
            const replacements = terms.map((term, index) => ({
                from: term.entity_name,
                to: selected[index]?.entity_name ?? term.entity_name,
            }));
            const resolvedUserInput = applyEntityDisambiguationSelections(
                originalUserContent,
                replacements,
            );

            const lookupItemsFromSelection: LookupItem[] = selected.map((m) => ({
                column_name: m.column_name,
                entity_name: m.entity_name,
            }));
            if (lookupItemsFromSelection.length > 0) {
                onLookupsChange?.(lookupItemsFromSelection);
            }

            onEntityDisambiguationContinue?.(selected, messageId, resolvedUserInput);

            // Auto-submit the resolved query only as a fallback: hosts that implement
            // onEntityDisambiguationContinue resume the interrupt themselves, and
            // submitting here too would fire a second request for the same turn.
            if (!onEntityDisambiguationContinue && onInputSubmit) {
                onInputSubmit(resolvedUserInput);
            }

            // Clear input either way — the clarified query has been handed off.
            if (onInputChange) {
                onInputChange('');
            } else {
                setInternalInputValue('');
            }
        },
        [onInputChange, onLookupsChange, onEntityDisambiguationContinue, onInputSubmit],
    );

    const commitAcceptedLookup = useCallback(
        (result: {
            nextValue: string;
            nextCaret: number;
            item: LookupItem;
            range: { start: number; end: number };
        }) => {
            const { nextValue, nextCaret, item, range } = result;
            lookupRanges.commitPick(inputValue, nextValue, {
                lookup: item,
                start: range.start,
                end: range.end,
            });
            if (onInputChange) {
                onInputChange(nextValue);
            } else {
                setInternalInputValue(nextValue);
            }
            pendingInputCaretRef.current = nextCaret;

            const prev = selectedLookupsRef.current ?? [];
            const exists = prev.some(
                (l) => l.column_name === item.column_name && l.entity_name === item.entity_name,
            );
            const nextSelected = exists ? prev : [...prev, item];
            if (isTimePeriodLookupItem(item)) {
                fireTimePeriodSelect(fireEvent, item, analyticsContext);
            } else {
                fireEntityFilterItemSelect(fireEvent, nextSelected, analyticsContext);
            }
        },
        [inputValue, lookupRanges, onInputChange, fireEvent, analyticsContext],
    );

    const acceptSlashSuggestion = useCallback(
        (index: number) => {
            const match =
                slash.mode === 'search'
                    ? slash.suggestions[index]
                    : slash.matchingValues[index];
            if (!match) return;
            const result = slash.accept(match.item);
            if (!result) return;
            commitAcceptedLookup(result);
        },
        [slash, commitAcceptedLookup],
    );

    const pickSlashCategoryById = useCallback(
        (categoryId: string) => {
            const cat = slash.categories.find((c) => c.id === categoryId);
            if (!cat) return;
            slash.pickCategory(cat.id);
            fireSlashCommandOptionSelect(fireEvent, cat.id, cat.label, analyticsContext);
        },
        [slash, fireEvent, analyticsContext],
    );

    const updateSlashQuery = useCallback(
        (query: string) => {
            const result = replaceSlashTokenQuery(inputValue, slash.range, query);
            if (!result) return;
            const { nextValue, nextCaret } = result;
            if (onInputChange) {
                onInputChange(nextValue);
            } else {
                setInternalInputValue(nextValue);
            }
            pendingInputCaretRef.current = nextCaret;
            setInputCaretPos(nextCaret);
        },
        [slash.range, inputValue, onInputChange],
    );

    const handleLookupSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex + 1);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex - 1);
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (slash.suggestions.length > 0) {
                    acceptSlashSuggestion(slash.highlightIndex);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                slash.goBack();
            }
        },
        [slash, acceptSlashSuggestion],
    );

    useEffect(() => {
        if (!slash.isActive) return;
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (target.closest('[data-portfolio-filter]')) return;
            if (target.closest('[data-lookup-search-input]')) return;
            if (
                inputTextareaRef.current &&
                (target === inputTextareaRef.current ||
                    inputTextareaRef.current.contains(target))
            ) {
                return;
            }
            slash.close();
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [slash.isActive, slash.close]);

    const deleteLookupTokenAtCaret = useCallback((): boolean => {
        const el = inputTextareaRef.current;
        if (!el) return false;
        if (el.selectionStart !== el.selectionEnd) return false;
        const caret = el.selectionStart ?? 0;
        const range = lookupRanges.findRangeAtCaret(caret);
        if (!range) return false;
        const nextValue = inputValue.slice(0, range.start) + inputValue.slice(range.end);
        // The deletion edit overlaps the entity range — rebase will drop the
        // range from state and emit a `onLookupsChange` update for the host.
        lookupRanges.onEdit(inputValue, nextValue);
        if (onInputChange) {
            onInputChange(nextValue);
        } else {
            setInternalInputValue(nextValue);
        }
        pendingInputCaretRef.current = range.start;
        return true;
    }, [inputValue, lookupRanges, onInputChange]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (slash.isActive) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    slash.setHighlightIndex(slash.highlightIndex + 1);
                    return;
                }
                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    slash.setHighlightIndex(slash.highlightIndex - 1);
                    return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    const activated = slash.activateHighlight();
                    if (!activated) return;
                    if (activated.kind === 'category') {
                        fireSlashCommandOptionSelect(
                            fireEvent,
                            activated.categoryId,
                            activated.label,
                            analyticsContext,
                        );
                        return;
                    }
                    commitAcceptedLookup(activated);
                    return;
                }
                if (e.key === 'Escape') {
                    e.preventDefault();
                    if (slash.mode === 'search' || slash.activeDimensionGroupId != null) {
                        slash.goBack();
                    } else {
                        slash.close();
                    }
                    return;
                }
            }
            // Atomic delete: when the caret sits at the right edge of a
            // confirmed entity range, a single Backspace nukes the whole entity
            // in one stroke and drops it from `selectedLookups`.
            if (e.key === 'Backspace' && !e.metaKey && !e.ctrlKey && !e.altKey) {
                if (deleteLookupTokenAtCaret()) {
                    e.preventDefault();
                    return;
                }
            }
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSubmit && onInputSubmit) {
                    handleInputSubmit();
                }
            }
        },
        [
            slash,
            commitAcceptedLookup,
            handleInputSubmit,
            canSubmit,
            onInputSubmit,
            deleteLookupTokenAtCaret,
            fireEvent,
            analyticsContext,
        ],
    );

    const handleInputFocus = useCallback(() => {
        setIsInputFocused(true);
        if (!hasLoadedLookupsRef.current) {
            hasLoadedLookupsRef.current = true;
            onLoadLookups?.();
        }
    }, [onLoadLookups]);

    useEffect(() => {
        if (inputCaretPos > inputValue.length) {
            setInputCaretPos(inputValue.length);
        }
    }, [inputValue, inputCaretPos]);

    const handleSaveAsCustom = useCallback(() => {
        if (hasInputValue && onSaveAsCustom) {
            onSaveAsCustom(inputValue);
            showSuccessNotification({
                message: 'Question saved',
                description: 'Your custom question has been saved successfully.',
                placement: 'bottomRight',
            });
        }
    }, [hasInputValue, onSaveAsCustom, inputValue, showSuccessNotification]);

    const handleQuestionBankClick = useCallback(() => {
        if (onQuestionBankClick) {
            onQuestionBankClick();
        }
    }, [onQuestionBankClick]);

    const handleViewArtefactFromPhase = useCallback(
        (artefactId: string, subtaskId: string, phaseId: string) => {
            if (onViewArtefact) {
                onViewArtefact(artefactId, subtaskId);
            }
        },
        [onViewArtefact],
    );

    // Conversation header: title on top, timestamp below (matches Figma design)
    // When showConversationTitleAsCard=true the title is rendered as the blue card below,
    // so displayTitle should NOT include conversationTitle for the header row.
    const displayTitle = headerTitle || (showConversationTitleAsCard ? undefined : conversationTitle);
    const hasConversationHeader = displayTitle || timestamp;
    const showHeaderSection = hasConversationHeader || showSidebarToggleInPanel;

    const renderConversationHeader = () => {
        if (!showHeaderSection) return null;

        // When showConversationTitleAsCard is true and there's no other title to show
        // in the header row, skip the title row entirely (unless the hamburger is shown).
        const showTitleRow = showSidebarToggleInPanel || !!displayTitle || !!onHeaderActionClick;

        return (
            <div className="flex-shrink-0 z-10 bg-white px-6 pt-2" data-test-id={`${dataTestId}-conversation-header`}>
                {/* Title row: optional hamburger (left) + title + action icon */}
                {showTitleRow && (
                    <div className="flex items-center gap-3 pb-4">
                        {showSidebarToggleInPanel && (
                            <Tooltip title="View all threads" placement="top" getPopupContainer={() => document.body}>
                                <button
                                    type="button"
                                    onClick={onSidebarToggleInPanel}
                                    className="flex items-center justify-center w-8 h-8 shrink-0 hover:bg-[rgba(0,0,0,0.04)] rounded transition-colors"
                                    aria-label="View all threads"
                                    data-test-id={`${dataTestId}-sidebar-toggle-in-panel`}
                                >
                                    <Menu size={20} color="rgba(0,0,0,0.65)" />
                                </button>
                            </Tooltip>
                        )}
                        {displayTitle ? (
                            <h2 className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-black flex-1 min-w-0 truncate">
                                {displayTitle}
                            </h2>
                        ) : (
                            showSidebarToggleInPanel && (
                                <h2 className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-black flex-1">
                                    Ask Asper
                                </h2>
                            )
                        )}
                        {onHeaderActionClick && (
                            <button
                                onClick={onHeaderActionClick}
                                className="flex items-center justify-center w-6 h-6 hover:bg-[rgba(0,0,0,0.04)] rounded transition-colors shrink-0"
                                data-test-id={`${dataTestId}-header-action`}
                                aria-label="Edit conversation"
                            >
                                <EditDoc size={16} color="rgba(0,0,0,0.85)" />
                            </button>
                        )}
                    </div>
                )}
                {/* Grey line */}
                <div className="border-b border-[rgba(0,0,0,0.06)]" />
                {/* Timestamp centered below the grey line */}
                {timestamp && (
                    <div className="flex justify-center pt-4 pb-4">
                        <span className="font-['Open_Sans'] text-[14px] leading-[20px] text-[rgba(0,0,0,0.45)]">
                            {formatTimestampForDisplay(timestamp)}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    // Group conversation messages into question-response pairs
    const groupMessagesIntoPairs = (messages: typeof conversationMessages) => {
        const pairs: Array<{ user: (typeof conversationMessages)[0]; assistant?: (typeof conversationMessages)[0] }> =
            [];
        let currentPair: {
            user: (typeof conversationMessages)[0];
            assistant?: (typeof conversationMessages)[0];
        } | null = null;

        for (const message of messages) {
            if (message.type === 'user') {
                // If there's an incomplete pair, save it
                if (currentPair) {
                    pairs.push(currentPair);
                }
                // Start a new pair
                currentPair = { user: message };
            } else if (message.type === 'assistant' && currentPair) {
                // Add assistant response to current pair
                currentPair.assistant = message;
                pairs.push(currentPair);
                currentPair = null;
            }
        }

        // Add any remaining incomplete pair
        if (currentPair) {
            pairs.push(currentPair);
        }

        return pairs;
    };

    const messagePairs = groupMessagesIntoPairs(conversationMessages);
    const usePairs = conversationPairs && conversationPairs.length > 0;
    const currentPairCount = usePairs ? (conversationPairs?.length ?? 0) : messagePairs.length;

    /** Detect if last pair is in "generating" state (user asked, waiting for/streaming response) */
    const isLastPairGenerating = (): boolean => {
        if (usePairs && conversationPairs && conversationPairs.length > 0) {
            const last = conversationPairs[conversationPairs.length - 1];
            const content = last.assistantMessage?.content;
            if (typeof content === 'string' && content === 'Generating...') return true;
            if (typeof content === 'string' && content.startsWith('Generating')) return true;
        }
        if (!usePairs && messagePairs.length > 0) {
            const last = messagePairs[messagePairs.length - 1];
            return !last.assistant;
        }
        return false;
    };

    /** Detect when user manually scrolls up - disable auto-scroll until next question */
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        // Ignore scroll events triggered by our own programmatic scroll (within 300ms)
        if (Date.now() - lastProgrammaticScrollRef.current < 300) return;
        const { scrollTop, clientHeight, scrollHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        if (distanceFromBottom > SCROLL_NEAR_BOTTOM_THRESHOLD) {
            userHasScrolledUpRef.current = true;
        }
    }, []);

    // Reactive autoscroll: new content -> scroll to bottom; new question -> scroll question to top
    // When user has manually scrolled up, skip auto-scroll until they ask a new question
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const prevCount = prevPairCountRef.current;
        const pairCountIncreased = currentPairCount > prevCount;
        const isGenerating = isLastPairGenerating();

        if (pairCountIncreased && isGenerating) {
            // New question just asked: re-enable auto-scroll and scroll so the asked question appears at top
            userHasScrolledUpRef.current = false;
            lastProgrammaticScrollRef.current = Date.now();
            requestAnimationFrame(() => {
                const lastPairUser = container.querySelector('[data-last-pair-user-message]');
                if (lastPairUser) {
                    const containerRect = container.getBoundingClientRect();
                    const elementRect = lastPairUser.getBoundingClientRect();
                    const targetScrollTop = container.scrollTop + (elementRect.top - containerRect.top);
                    container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
                }
            });
        } else if (!userHasScrolledUpRef.current) {
            // New content arrived (streaming or completed): scroll to bottom only if user hasn't scrolled up
            lastProgrammaticScrollRef.current = Date.now();
            requestAnimationFrame(() => {
                container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
            });
        }

        prevPairCountRef.current = currentPairCount;
    }, [
        currentPairCount,
        conversationPairs,
        messagePairs,
        usePairs,
        runningTasks,
        plannerTasks,
        phaseCards,
        hitlCards,
        assessmentStatus,
        assessmentContent,
        suggestedActions,
        bottomContent,
    ]);

    const handlePopulateInput = useCallback(
        (value: string) => {
            if (onInputChange) {
                onInputChange(value);
            } else {
                setInternalInputValue(value);
            }
        },
        [onInputChange],
    );

    return (
        <ConfigProvider theme={theme}>
            <style>{`
                @keyframes finalising-shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -100% 0; }
                }
            `}</style>
            <div
                className="flex flex-col h-full bg-white rounded-[12px] overflow-hidden"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-left-panel
            >
                {/* Fixed header at top - outside scroll container so it stays fixed when scrolling */}
                {showHeaderSection && renderConversationHeader()}

                {/* Scrollable Content Area */}
                <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto min-h-0">
                    <div className="py-6 space-y-6">
                        {/* Conversation Title Card (standalone mode - shown when not in header, or when showConversationTitleAsCard forces card rendering) */}
                        {conversationTitle && (!displayTitle || showConversationTitleAsCard) && (
                            <div className="px-6" data-test-id={`${dataTestId}-conversation-title-section`}>
                                <div
                                    className="bg-white border border-white rounded-lg flex items-center gap-2.5 p-4"
                                    style={{
                                        boxShadow: '0px 2.461px 19.691px 0px rgba(99, 140, 243, 0.24)',
                                    }}
                                    data-test-id={`${dataTestId}-conversation-title-card`}
                                >
                                    <span className="font-['Open_Sans'] font-medium text-[14px] leading-normal text-[#4557d5] overflow-hidden text-ellipsis">
                                        {conversationTitle}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Standalone Summary Card (only in standalone agentic layout) */}
                        {standaloneSummary && (
                            <div className="px-6">
                                <StandaloneSummaryCard
                                    summary={standaloneSummary}
                                    timestamp={standaloneSummaryTimestamp}
                                    followUpQuestions={standaloneFollowUpQuestions}
                                    onFollowUpClick={handlePopulateInput}
                                    data-test-id={`${dataTestId}-standalone-summary`}
                                />
                            </div>
                        )}

                        {usePairs ? (
                            /* Per-pair rendering (new format from messages) */
                            conversationPairs!.map((pair, pairIndex) => {
                                const isLastPair = pairIndex === conversationPairs!.length - 1;
                                const isGenerating =
                                    isLastPair &&
                                    (() => {
                                        const c = pair.assistantMessage?.content;
                                        return (
                                            typeof c === 'string' &&
                                            (c === 'Generating...' || c.startsWith('Generating'))
                                        );
                                    })();
                                const pairTypewriter = enableTypewriter && !pair.disableTypewriter;
                                const userContent =
                                    typeof pair.userMessage?.content === 'string'
                                        ? pair.userMessage.content.trim()
                                        : '';
                                // Hide empty / synthetic resume bubbles (e.g. clarification "proceed").
                                const showUserMessage =
                                    userContent.length > 0 && userContent.toLowerCase() !== 'proceed';
                                const UserMessageWrapper = !showUserMessage ? null : isGenerating ? (
                                    <div key={`user-wrapper-${pairIndex}`} data-last-pair-user-message>
                                        <ConversationMessage
                                            message={pair.userMessage}
                                            enableTypewriter={pairTypewriter}
                                            typewriterSpeed={typewriterSpeed}
                                            data-test-id={`${dataTestId}-conversation-message-user-${pairIndex}`}
                                        />
                                    </div>
                                ) : (
                                    <ConversationMessage
                                        key={pair.userMessage.id || `user-${pairIndex}`}
                                        message={pair.userMessage}
                                        enableTypewriter={pairTypewriter}
                                        typewriterSpeed={typewriterSpeed}
                                        data-test-id={`${dataTestId}-conversation-message-user-${pairIndex}`}
                                    />
                                );
                                return (
                                    <div key={`pair-${pairIndex}`} className="space-y-6">
                                        <div className="px-6 space-y-6">
                                            {UserMessageWrapper}
                                            <ConversationMessage
                                                key={pair.assistantMessage.id || `assistant-${pairIndex}`}
                                                message={pair.assistantMessage}
                                                enableTypewriter={pairTypewriter}
                                                typewriterSpeed={typewriterSpeed}
                                                data-test-id={`${dataTestId}-conversation-message-assistant-${pairIndex}`}
                                            />
                                            {pair.entityDisambiguation && (
                                                <EntityDisambiguationCard
                                                    terms={pair.entityDisambiguation.terms}
                                                    initialSelections={
                                                        pair.entityDisambiguation.initialSelections
                                                    }
                                                    resolved={pair.entityDisambiguation.resolved}
                                                    lookupItems={lookupItems}
                                                    lookupCategories={lookupCategories}
                                                    onLoadLookups={onLoadLookups}
                                                    onContinue={(selected) =>
                                                        handleEntityDisambiguationContinue(
                                                            selected,
                                                            pair.entityDisambiguation?.messageId,
                                                            pair.entityDisambiguation?.terms ?? [],
                                                            typeof pair.userMessage?.content ===
                                                                'string'
                                                                ? pair.userMessage.content
                                                                : '',
                                                        )
                                                    }
                                                    onSkip={() =>
                                                        onEntityDisambiguationSkip?.(
                                                            pair.entityDisambiguation?.messageId,
                                                        )
                                                    }
                                                    data-test-id={`${dataTestId}-entity-disambiguation-${pairIndex}`}
                                                />
                                            )}
                                            {pair.outOfScopeFeedback && (
                                                <OutOfScopeFeedbackCard
                                                    feedback={pair.outOfScopeFeedback}
                                                    data-test-id={`${dataTestId}-out-of-scope-feedback-${pairIndex}`}
                                                />
                                            )}
                                        </div>

                                        {pair.plannerTasks && (
                                            <div className="px-6">
                                                <PlannerTasksBox
                                                    plannerTasks={pair.plannerTasks}
                                                    data-test-id={`${dataTestId}-planner-tasks-${pairIndex}`}
                                                />
                                            </div>
                                        )}

                                        {pair.phaseCards.length > 0 && (
                                            <div className="px-6 space-y-4">
                                                {pair.phaseCards.map((phase, index) => (
                                                    <PhaseCard
                                                        key={phase.id || index}
                                                        phase={phase}
                                                        onViewArtefact={handleViewArtefactFromPhase}
                                                        onToggleCollapsed={onPhaseToggleCollapsed}
                                                        defaultCollapsed={!!pair.summaryText}
                                                        data-test-id={`${dataTestId}-phase-card-${pairIndex}-${index}`}
                                                    />
                                                ))}
                                            </div>
                                        )}

                                        {/* Summary text (overview or content) - or "Finalising" shimmer only while generating after phase cards appear */}
                                        {(pair.summaryText ||
                                            (isLastPair &&
                                                !pair.summaryText &&
                                                pair.phaseCards.length > 0 &&
                                                isGenerating)) && (
                                            <div
                                                className="px-6 font-['Open_Sans'] text-[16px] leading-[24px] text-[#52525C] markdown-content"
                                                data-test-id={`${dataTestId}-summary-text-${pairIndex}`}
                                            >
                                                {!pair.summaryText ? (
                                                    /* "Finalising" shimmer - grey text with wave shimmer L→R */
                                                    <span
                                                        className="inline-block font-['Open_Sans'] text-[16px] leading-[24px]"
                                                        style={
                                                            {
                                                                background:
                                                                    'linear-gradient(90deg, #71717a 0%, #71717a 35%, #a1a1aa 50%, #71717a 65%, #71717a 100%)',
                                                                backgroundSize: '200% 100%',
                                                                WebkitBackgroundClip: 'text',
                                                                backgroundClip: 'text',
                                                                color: 'transparent',
                                                                animation: 'finalising-shimmer 2s ease-in-out infinite',
                                                            } as React.CSSProperties
                                                        }
                                                    >
                                                        Finalising
                                                    </span>
                                                ) : (
                                                    (() => {
                                                        // Use ReactMarkdown when text contains color spans so red/green pills render.
                                                        // TypewriterText does not parse HTML and would show spans as literal text.
                                                        const hasColorSpans =
                                                            /<span\s[^>]*style=["'][^"']*color\s*:\s*(?:red|green)/i.test(
                                                                pair.summaryText,
                                                            );
                                                        const useReactMarkdown = hasColorSpans || !pairTypewriter;
                                                        return useReactMarkdown ? (
                                                            <ReactMarkdown
                                                                rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
                                                                remarkPlugins={[remarkGfm]}
                                                                remarkRehypeOptions={{ allowDangerousHtml: true }}
                                                                components={{
                                                                    h1: ({ node, ...props }) => (
                                                                        <h1
                                                                            className="font-['Open_Sans'] font-bold text-[20px] leading-[28px] text-[rgba(0,0,0,0.88)] mb-3 mt-4 first:mt-0"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    h2: ({ node, ...props }) => (
                                                                        <h2
                                                                            className="font-['Open_Sans'] font-bold text-[18px] leading-[26px] text-[rgba(0,0,0,0.88)] mb-3 mt-4 first:mt-0"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    h3: ({ node, ...props }) => (
                                                                        <h3
                                                                            className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-3 first:mt-0"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    h4: ({ node, ...props }) => (
                                                                        <h4
                                                                            className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-3 first:mt-0"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    h5: ({ node, ...props }) => (
                                                                        <h5
                                                                            className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-2 first:mt-0"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    h6: ({ node, ...props }) => (
                                                                        <h6
                                                                            className="font-['Open_Sans'] font-bold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 mt-2 first:mt-0"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    p: ({ node, ...props }) => (
                                                                        <p
                                                                            className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 m-0"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    ul: ({ node, ...props }) => (
                                                                        <ul
                                                                            className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 ml-4 list-disc"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    ol: ({ node, ...props }) => (
                                                                        <ol
                                                                            className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] mb-2 ml-4 list-decimal"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    li: ({ node, ...props }) => (
                                                                        <li className="mb-1" {...props} />
                                                                    ),
                                                                    table: ({ node, ...props }) => (
                                                                        <div className="overflow-x-auto my-3">
                                                                            <table
                                                                                className="min-w-full border-collapse border border-[rgba(0,0,0,0.06)]"
                                                                                {...props}
                                                                            />
                                                                        </div>
                                                                    ),
                                                                    thead: ({ node, ...props }) => (
                                                                        <thead
                                                                            className="bg-[rgba(0,0,0,0.02)]"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    tbody: ({ node, ...props }) => <tbody {...props} />,
                                                                    tr: ({ node, ...props }) => (
                                                                        <tr
                                                                            className="border-b border-[rgba(0,0,0,0.06)]"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    th: ({ node, ...props }) => (
                                                                        <th
                                                                            className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-3 py-2 text-left border border-[rgba(0,0,0,0.06)]"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    td: ({ node, ...props }) => (
                                                                        <td
                                                                            className="font-['Open_Sans'] text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-3 py-2 border border-[rgba(0,0,0,0.06)]"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    span: ({ node, ...props }: any) => {
                                                                        const styleAttr =
                                                                            (node?.properties as any)?.style ??
                                                                            (props as any).style;
                                                                        const children = props.children;
                                                                        const textContent =
                                                                            extractTextContent(children);

                                                                        // Normalize color: hast gives string "color:red", React gets object { color: "red" }
                                                                        let colorValue: string | undefined;
                                                                        if (typeof styleAttr === 'string') {
                                                                            const match =
                                                                                styleAttr.match(/color\s*:\s*([^;]+)/i);
                                                                            if (match) colorValue = match[1].trim();
                                                                        } else if (
                                                                            styleAttr &&
                                                                            typeof styleAttr === 'object' &&
                                                                            'color' in styleAttr
                                                                        ) {
                                                                            colorValue = String(styleAttr.color);
                                                                        }

                                                                        if (colorValue) {
                                                                            const isRed =
                                                                                /^(red|#ec343e|#ff0000|#f00|rgb\s*\(\s*255\s*,?\s*0\s*,?\s*0\s*\))$/i.test(
                                                                                    colorValue.trim(),
                                                                                );
                                                                            const isGreen =
                                                                                /^(green|#00a63e|#389e0d|#0f0|rgb\s*\(\s*0\s*,?\s*166\s*,?\s*62\s*\))$/i.test(
                                                                                    colorValue.trim(),
                                                                                );
                                                                            if (isRed)
                                                                                return (
                                                                                    <HighlightBadge
                                                                                        value={textContent.trim()}
                                                                                        type="negative"
                                                                                    />
                                                                                );
                                                                            if (isGreen)
                                                                                return (
                                                                                    <HighlightBadge
                                                                                        value={textContent.trim()}
                                                                                        type="positive"
                                                                                    />
                                                                                );
                                                                        }

                                                                        return <span {...props} />;
                                                                    },
                                                                    code: ({ node, ...props }: any) => {
                                                                        const isInline = (props as any).inline;
                                                                        const codeContent = String(
                                                                            props.children || '',
                                                                        );

                                                                        const badgeMatch = codeContent.match(
                                                                            /^badge:(positive|negative):(.+)$/,
                                                                        );
                                                                        if (badgeMatch) {
                                                                            const badgeType = badgeMatch[1] as
                                                                                | 'positive'
                                                                                | 'negative';
                                                                            const badgeValue = badgeMatch[2];
                                                                            return (
                                                                                <HighlightBadge
                                                                                    value={badgeValue}
                                                                                    type={badgeType}
                                                                                />
                                                                            );
                                                                        }

                                                                        return isInline ? (
                                                                            <code
                                                                                className="bg-[rgba(0,0,0,0.06)] px-1 py-0.5 rounded text-[13px] font-mono"
                                                                                {...props}
                                                                            />
                                                                        ) : (
                                                                            <code
                                                                                className="block bg-[rgba(0,0,0,0.06)] p-3 rounded my-2 text-[13px] font-mono overflow-x-auto"
                                                                                {...props}
                                                                            />
                                                                        );
                                                                    },
                                                                    pre: ({ node, ...props }) => (
                                                                        <pre
                                                                            className="bg-[rgba(0,0,0,0.06)] p-3 rounded my-2 overflow-x-auto"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    blockquote: ({ node, ...props }) => (
                                                                        <blockquote
                                                                            className="border-l-4 border-[rgba(0,0,0,0.2)] pl-4 my-2 italic text-[rgba(0,0,0,0.65)]"
                                                                            {...props}
                                                                        />
                                                                    ),
                                                                    strong: ({ node, ...props }) => (
                                                                        <strong className="font-bold" {...props} />
                                                                    ),
                                                                    em: ({ node, ...props }) => (
                                                                        <em className="italic" {...props} />
                                                                    ),
                                                                }}
                                                            >
                                                                {fixUnclosedColorSpans(pair.summaryText)}
                                                            </ReactMarkdown>
                                                        ) : (
                                                            <TypewriterText
                                                                text={pair.summaryText}
                                                                speed={typewriterSpeed}
                                                                parseBold
                                                                contentKey={
                                                                    pair.assistantMessage?.id != null
                                                                        ? `summary-${pair.assistantMessage.id}`
                                                                        : undefined
                                                                }
                                                            />
                                                        );
                                                    })()
                                                )}
                                            </div>
                                        )}

                                        {isLastPair && pair.suggestedActions.length > 0 && (
                                            <div className="px-6">
                                                <FollowupQuestions
                                                    actions={pair.suggestedActions}
                                                    onPopulateInput={handlePopulateInput}
                                                    onPillClick={onSuggestedActionClick}
                                                    data-test-id={`${dataTestId}-followup-questions-${pairIndex}`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            /* Legacy flat rendering */
                            <>
                                {messagePairs.map((pair, pairIndex) => {
                                    const isLastLegacyPair = pairIndex === messagePairs.length - 1;
                                    const isLegacyGenerating = isLastLegacyPair && !pair.assistant;
                                    const LegacyUserWrapper = isLegacyGenerating ? (
                                        <div data-last-pair-user-message>
                                            <ConversationMessage
                                                message={pair.user}
                                                enableTypewriter={enableTypewriter}
                                                typewriterSpeed={typewriterSpeed}
                                                data-test-id={`${dataTestId}-conversation-message-user-${pairIndex}`}
                                            />
                                        </div>
                                    ) : (
                                        <ConversationMessage
                                            key={pair.user.id || `user-${pairIndex}`}
                                            message={pair.user}
                                            enableTypewriter={enableTypewriter}
                                            typewriterSpeed={typewriterSpeed}
                                            data-test-id={`${dataTestId}-conversation-message-user-${pairIndex}`}
                                        />
                                    );
                                    return (
                                        <div key={`pair-${pairIndex}`} className="space-y-6">
                                            <div className="px-6 space-y-6">
                                                {LegacyUserWrapper}
                                                {pair.assistant && (
                                                    <ConversationMessage
                                                        key={pair.assistant.id || `assistant-${pairIndex}`}
                                                        message={pair.assistant}
                                                        enableTypewriter={enableTypewriter}
                                                        typewriterSpeed={typewriterSpeed}
                                                        data-test-id={`${dataTestId}-conversation-message-assistant-${pairIndex}`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {plannerTasks && (
                                    <div className="px-6">
                                        <PlannerTasksBox
                                            plannerTasks={plannerTasks}
                                            data-test-id={`${dataTestId}-planner-tasks`}
                                        />
                                    </div>
                                )}

                                {hitlCards.length > 0 && (
                                    <div className="px-6 space-y-6">
                                        {hitlCards.map((hitlCard, index) => (
                                            <HITLCard
                                                key={hitlCard.id || index}
                                                card={hitlCard}
                                                data-test-id={`${dataTestId}-hitl-card-${index}`}
                                            />
                                        ))}
                                    </div>
                                )}

                                {phaseCards.length > 0 && (
                                    <div className="px-6 space-y-4">
                                        {phaseCards.map((phase, index) => (
                                            <PhaseCard
                                                key={phase.id || index}
                                                phase={phase}
                                                onViewArtefact={handleViewArtefactFromPhase}
                                                onToggleCollapsed={onPhaseToggleCollapsed}
                                                data-test-id={`${dataTestId}-phase-card-${index}`}
                                            />
                                        ))}
                                    </div>
                                )}

                                {(assessmentStatus || assessmentContent) && (
                                    <div className="px-6">
                                        <AssessmentMessage
                                            status={assessmentStatus}
                                            content={assessmentContent}
                                            data-test-id={`${dataTestId}-assessment-message`}
                                        />
                                    </div>
                                )}

                                {suggestedActions.length > 0 && (
                                    <div className="px-6">
                                        <FollowupQuestions
                                            actions={suggestedActions}
                                            onPopulateInput={handlePopulateInput}
                                            onPillClick={onSuggestedActionClick}
                                            data-test-id={`${dataTestId}-followup-questions`}
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {/* Bottom content slot (e.g. View Artefacts button) */}
                        {bottomContent && (
                            <div className="px-6" data-test-id={`${dataTestId}-bottom-content`}>
                                {bottomContent}
                            </div>
                        )}
                    </div>
                </div>

                {/* Sticky Bottom Input Bar — flex-shrink-0 so overflow-hidden on the column does not clip disclaimer text */}
                <div className="sticky bottom-0 shrink-0 bg-white px-6 py-2 pb-3 z-10">
                    <div
                        className={`relative bg-white border border-white rounded-[15px] flex flex-col h-[160px] justify-between p-2 w-full ${
                            isInputFocused
                                ? 'shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.7)]'
                                : hasInputValue
                                  ? 'shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.5)]'
                                  : 'shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.32)]'
                        }`}
                        data-test-id={`${dataTestId}-input-bar`}
                    >
                        {slash.isActive && slash.mode === 'category' ? (
                            <LookupCategoryDropdown
                                scoredCategories={slash.scoredCategories}
                                matchingValues={slash.matchingValues}
                                highlightIndex={slash.highlightIndex}
                                onHighlight={slash.setHighlightIndex}
                                onSelectCategory={pickSlashCategoryById}
                                onSelectValue={acceptSlashSuggestion}
                                query={slash.query}
                                hasNoMatches={slash.hasNoMatches}
                                contextLabel={slash.activeDimensionGroupLabel}
                                onBack={
                                    slash.activeDimensionGroupId != null
                                        ? slash.goBack
                                        : undefined
                                }
                                data-test-id={
                                    dataTestId ? `${dataTestId}-lookup-category-dropdown` : undefined
                                }
                            />
                        ) : slash.isActive ? (
                            <LookupSuggestionDropdown
                                suggestions={slash.suggestions}
                                highlightIndex={slash.highlightIndex}
                                onHighlight={slash.setHighlightIndex}
                                onSelect={acceptSlashSuggestion}
                                categoryLabel={slash.activeCategoryLabel}
                                query={slash.query}
                                onQueryChange={updateSlashQuery}
                                onSearchKeyDown={handleLookupSearchKeyDown}
                                onBack={slash.goBack}
                                data-test-id={dataTestId ? `${dataTestId}-lookup-dropdown` : undefined}
                            />
                        ) : null}
                        {/* Search Input Area */}
                        <div className="flex gap-2 items-start py-[7px] w-full">
                            <div className="shrink-0">
                                <Search size={20} color="rgba(0,0,0,0.85)" />
                            </div>
                            {/* Relative wrapper for textarea + invisible mirror
                             *  that draws highlight pills behind confirmed
                             *  entity ranges — see `AgenticSearchInput` for the
                             *  layout contract. */}
                            <div className="relative flex-1 min-h-[80px]">
                                <LookupHighlightLayer
                                    value={inputValue}
                                    ranges={lookupRanges.ranges}
                                    textareaRef={inputTextareaRef}
                                    mirrorClassName={LOOKUP_INPUT_TYPOGRAPHY}
                                    data-test-id={
                                        dataTestId ? `${dataTestId}-highlight-layer` : undefined
                                    }
                                />
                                <textarea
                                    ref={inputTextareaRef}
                                    className={`relative w-full ${LOOKUP_INPUT_TYPOGRAPHY} text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.45)] border-0 outline-none resize-none bg-transparent`}
                                    placeholder={inputPlaceholder}
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    onKeyUp={syncInputCaretFromTextarea}
                                    onClick={syncInputCaretFromTextarea}
                                    onSelect={syncInputCaretFromTextarea}
                                    onFocus={handleInputFocus}
                                    onBlur={() => setIsInputFocused(false)}
                                    disabled={inputDisabled}
                                    data-test-id={`${dataTestId}-textarea`}
                                />
                            </div>
                        </div>

                        {/* Bottom Actions */}
                        <div className="flex items-center justify-between w-full gap-2">
                            {/* Left Actions */}
                            <div className="flex gap-2 items-center min-w-0">
                                {/* Question Bank Button */}
                                <Tooltip
                                    title="Browse sample questions"
                                    placement="top"
                                    getPopupContainer={() => document.body}
                                >
                                    <button
                                        type="button"
                                        className="bg-white border border-[#d0d5f5] rounded-lg flex gap-1.5 items-center h-10 px-3 cursor-pointer hover:bg-[#fafafa] transition-colors overflow-hidden relative whitespace-nowrap"
                                        onClick={handleQuestionBankClick}
                                        data-test-id={`${dataTestId}-question-bank`}
                                        aria-label="Browse sample questions"
                                    >
                                        <span className="shrink-0">
                                            <QuestionCircleOutlined size={16} color="#3b2c77" />
                                        </span>
                                        <span className="font-['Open_Sans'] font-normal text-[14px] leading-5 text-[#3b2c77] relative z-10 truncate">
                                            Question Bank
                                        </span>
                                    </button>
                                </Tooltip>

                                {/* Save as Custom Button */}
                                <Tooltip title="Save as template" placement="top" getPopupContainer={() => document.body}>
                                    <span className="inline-flex">
                                        <button
                                            type="button"
                                            className={`bg-white border border-[#e8eafa] rounded-lg flex gap-1.5 items-center h-10 px-3 transition-colors overflow-hidden relative whitespace-nowrap ${
                                                hasInputValue
                                                    ? 'cursor-pointer hover:bg-[#fafafa]'
                                                    : 'cursor-not-allowed opacity-60'
                                            }`}
                                            onClick={handleSaveAsCustom}
                                            disabled={!hasInputValue}
                                            data-test-id={`${dataTestId}-save-custom`}
                                            aria-label="Save as template"
                                        >
                                            <span className="shrink-0">
                                                <Bookmark
                                                    size={16}
                                                    color={hasInputValue ? '#3B2C77' : 'rgba(0,0,0,0.25)'}
                                                />
                                            </span>
                                            <span
                                                className={`font-['Open_Sans'] font-normal text-[14px] leading-5 relative z-10 truncate ${
                                                    hasInputValue ? 'text-[#3b2c77]' : 'text-[rgba(0,0,0,0.25)]'
                                                }`}
                                            >
                                                Save as custom
                                            </span>
                                        </button>
                                    </span>
                                </Tooltip>
                            </div>

                            {/* Send Button */}
                            <button
                                type="button"
                                className={`flex items-center gap-0.5 rounded px-3 py-1 pl-3 pr-2.5 transition-colors shrink-0 ${
                                    canSubmit
                                        ? 'bg-[#3B2C77] cursor-pointer hover:bg-gray-800'
                                        : 'bg-black/50 cursor-not-allowed'
                                }`}
                                onClick={handleInputSubmit}
                                disabled={!canSubmit}
                                data-test-id={`${dataTestId}-send`}
                            >
                                <span className="font-['Open_Sans'] font-normal text-[14px] leading-5 text-[#fbfaff] pr-1">
                                    Send
                                </span>
                                <Send size={20} color="#fbfaff" />
                            </button>
                        </div>
                    </div>
                    {searchDisclaimer != null && searchDisclaimer !== '' && (
                        <div
                            className="font-['Open_Sans'] font-normal text-xs leading-[18px] text-center text-[rgba(0,0,0,0.65)] px-2 w-full mt-2"
                            data-test-id={`${dataTestId}-search-disclaimer`}
                        >
                            {searchDisclaimer}
                        </div>
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AgenticLeftPanel;
